/**
 * What a plant's About sheet shows: its temperature and water scales, said
 * by the server (scale; services/plants/aboutScale, audit batch 4, A27: the
 * app worked them out from its own copies of the grading), its water facts
 * and its feeding. See the backend's routes/plantAbout.js.
 *
 * Endpoint: GET /plant-about/:plantObjId?lang=&unit= → { success, scale, water, feeding }.
 * Who uses it: features/plant-list PlantAboutSheet, which caches it under
 * ['plant-about', plantObjId, lang, unit] while the sheet is open. Auth: the
 * signed-in token, in the garden being tended.
 *
 * @module api/plantAbout
 */

import { apiClient } from './apiClient';
import type { VerdictToken } from '@/shared/theme/verdictTokens';

/** The crop's water facts, from its water data. Any may be missing. */
export interface PlantWaterFacts {
  /** Water it needs a week, in inches. */
  weeklyNeedsInches: number | null;
  /** The most rain a week it can take, in inches. */
  maxWeeklyRainInches: number | null;
  /** Rain in one day past which it is a downpour for it, in inches. */
  heavyRainDayInches: number | null;
  /** Hours it can stand in water before its roots suffer. */
  standingWaterHours: number | null;
  /** How much more it needs in a container, e.g. 1.6. */
  containerMultiplier: number | null;
  /** How much mulch cuts watering, e.g. 0.3 for a third. */
  mulchReduction: number | null;
  /** True for a crop that wants a deep soak rather than a little and often. */
  deepWateringPreferred: boolean | null;
}

/** The plant group's feeding, in the printed planner's own sentence. */
export interface PlantFeeding {
  /** Whether its group is a heavy feeder. */
  heavyFeeder: boolean;
  /** Weeks between feeds, for a heavy feeder. */
  everyWeeks: number | null;
  /** "Feed every 2 weeks once it's actively growing, until frost. …" In English. */
  sentence: string;
}

/** One line of a scale, as the server says it. */
export interface AboutScaleRow {
  /** Unique within its scale. */
  key: string;
  /** Its swatch: a verdict colour, drawn filled, tinted, or tinted with a ring (harm), as the grid draws the same meaning. */
  swatch: { token: VerdictToken; look: 'fill' | 'tint' | 'ring' };
  /** The readings: "86–99°F", "Nights under 50°F". */
  reading: string;
  /** What they mean: "Grows slowly". */
  meaning: string;
  /** The row where it grows best, or what it needs. */
  best: boolean;
}

/** The sheet's scales, said. */
export interface AboutScale {
  /** "tender", "warm-season", after the Temperature heading. */
  classWords: string[];
  /** Hottest first, down to what harms it; empty without a range. */
  temperature: AboutScaleRow[];
  /** What it needs a week, the most rain a week it can take, a downpour in a day. */
  water: AboutScaleRow[];
  /** Standing water, a container, mulch, deep watering, in a sentence or two; '' for none. */
  waterNotes: string;
}

/** What GET /api/plant-about/:plantObjId answers. */
export interface PlantAbout {
  /** The scales (services/plants/aboutScale). */
  scale: AboutScale;
  /** Null when the crop has no water data. */
  water: PlantWaterFacts | null;
  /** Null when its group has no feeding sentence. */
  feeding: PlantFeeding | null;
}

/**
 * A plant's scales, water facts and feeding.
 *
 * @param plantObjId - The plant.
 * @param lang - The words' language.
 * @param unit - The reader's unit.
 * @returns Each, null when missing (or absent from the answer).
 */
export async function getPlantAbout(plantObjId: number, lang: string, unit: 'F' | 'C'): Promise<PlantAbout> {
  const response = await apiClient.get<PlantAbout & { success: boolean }>(`/plant-about/${plantObjId}`, { params: { lang, unit } });
  return { scale: response.data.scale ?? null, water: response.data.water ?? null, feeding: response.data.feeding ?? null };
}
